<script setup lang="ts">
import { computed, onMounted, ref, provide } from 'vue';

import { getLocale, getTranslation } from '@/utils/data-display-utils';
import { Module, ProjectModuleConsultationConfigDto } from '@/store/module.interface';
import { useCommunityStore } from '@/store/communityStore';
import { Consultation } from '@/store/consultation.interface';
import { intlFormat } from 'date-fns';
import { getLabel } from '@/utils/data-display-utils';
import { useRouter } from 'vue-router';
import { useUserStore } from '@/store/userStore';
import { consultationsService } from '@/services/consultations.service';
import { useTracker } from '@/composables/useTracker';
import { getAltText } from '@/utils/image.helper';
import { Locales } from '@/store/language.interface';

const isAllowed = ref<boolean>(false);
const isCompleted = ref<boolean>(false);

// CONST
const communityStore = useCommunityStore();
const userStore = useUserStore();
const router = useRouter();
const { track } = useTracker();

// PROPS
const props = defineProps<{
  module: Module;
  languageEnabled?: Locales[];
}>();

const moduleConfig = props.module.config as ProjectModuleConsultationConfigDto;

// LIFECYCLE
onMounted(async () => {
  await consultationsService.getConsultationBySlug(moduleConfig.slug);
  if (consultation.value) {
    isAllowed.value =
      communityStore.isUserAllowedAccessConsultation(consultation.value, userStore.user).allowed &&
      communityStore.isUserAllowedIfPrelaunchConsultation(consultation.value);
    isCompleted.value = communityStore.isConsultationCompleted(consultation.value);
  }
});

// COMPUTED
const consultation = computed<Consultation>(() => {
  return communityStore.community.consultations.find((consultation) => consultation.id === moduleConfig.consultationId);
});

const getBanner = computed(() => {
  if (consultation.value) {
    return consultation.value && consultation.value.bannerFile?.url
      ? consultation.value.bannerFile
      : communityStore.community.bannerFile;
  }
  return null;
});

// FUNCTIONS
function formatDate(date: Date) {
  return intlFormat(date, { dateStyle: 'long' }, { locale: getLocale(props.languageEnabled) });
}

function openConsultation() {
  trackConsultation();
  router.push({
    name: 'consultations',
    params: { slug: consultation.value.slug },
    query: { project_id: props.module.project_id },
  });
}

function openConsultationResults() {
  provide('results', true);
  trackConsultation();
  router.push({
    name: 'consultations',
    params: { slug: consultation.value.slug },
    query: { project_id: props.module.project_id },
  });
}

function trackConsultation() {
  track({
    event: 'view_consultation',
    params: {
      consultation_id: consultation.value.id,
      consultation_slug: consultation.value.slug,
    },
    trackWith: { facebook: true },
  });
}
</script>

<template>
  <div v-if="isAllowed" class="consultation-module-wrapper">
    <div class="consultation-module highlight">
      <div v-if="getBanner" class="consultation-module__banner">
        <img :src="getBanner.url" :alt="getAltText(getBanner, 'alt-text.consultation.banner', languageEnabled)" />
      </div>
      <div class="consultation-module__title">
        <h2>{{ getTranslation(props.module, 'title', languageEnabled) }}</h2>
      </div>
      <div class="consultation-module__content">
        <div class="consultation-module__content-left">
          <div v-if="consultation.beginDate && consultation.endDate" class="consultation-module__content-date">
            {{ formatDate(consultation.beginDate) }} - {{ formatDate(consultation.endDate) }}
          </div>
          <div v-else-if="consultation.endDate" class="consultation-module__content-date">
            {{ getLabel('consultation.end-date', props.languageEnabled) }}: {{ formatDate(consultation.endDate) }}
          </div>
          <div class="consultation-module__content-bottom">
            <q-chip :ripple="false" square size="md" :label="getLabel('consultation.public', props.languageEnabled)" />
          </div>
        </div>
        <div class="consultation-module__content-right">
          <q-btn v-if="!isCompleted" fab color="secondary" @click="openConsultation">
            <span class="q-mx-sm">{{ getLabel('buttons.participate', props.languageEnabled) }}</span>
          </q-btn>
          <q-btn v-else fab color="secondary" @click="openConsultationResults">
            <span class="q-mx-sm">{{ getLabel('buttons.see-results', props.languageEnabled) }}</span>
          </q-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.consultation-module-wrapper {
  width: 100%;

  .highlight {
    background-color: $color-neutral-grey-blue-150;
    border-radius: 8px;
    padding: $space-lg;
  }

  .consultation-module {
    display: flex;
    flex-direction: column;

    &__banner {
      img {
        width: 100%;
        border-radius: 8px;
      }
    }

    h2 {
      margin-bottom: $space-sm !important;
    }

    &__title {
      padding: $space-sm 0 0 0;
    }

    &__content {
      display: flex;
      flex-direction: row;

      &-left {
        width: 70%;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
      }

      &-right {
        width: 30%;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      &-date {
        color: $color-neutral-grey-400;
      }

      &-bottom {
        display: flex;
        justify-content: space-between;
        padding: $space-sm 0 0 0;

        .q-chip {
          background-color: #adc2d2;
          margin: 0;
        }
      }
    }
  }
}

@media screen and (max-width: $breakpoint-md) {
  .consultation-module-wrapper {
    .consultation-module {
      &__content {
        flex-direction: column;

        &-left {
          width: 100%;
        }
        &-right {
          width: 100%;
          display: flex;
          justify-content: center;
        }
      }
      &__content-bottom {
        .q-chip {
          font-size: $body-text !important;
          margin-bottom: $space-md;
          width: fit-content;
        }
        flex-direction: column;
      }
    }
  }
}
</style>
